import { useEffect, useRef, useState } from 'react';
import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';

/** Shared primitives. Everything the app renders should come from here, so the
 *  wizard and approval screens inherit the look rather than reinventing it. */

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ');

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  ref?: Ref<HTMLButtonElement>;
};

const BUTTON_BASE =
  'press inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

const BUTTON_VARIANTS: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-brand-800 text-white hover:bg-brand-700 dark:bg-brand-600 dark:hover:bg-brand-500',
  secondary:
    'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 ' +
    'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800',
  ghost: 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
  danger: 'bg-red-600 text-white hover:bg-red-700',
};

const BUTTON_SIZES: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2.5 text-sm',
};

export function Button({ variant = 'primary', size = 'md', className, ref, ...props }: ButtonProps) {
  return (
    <button
      type="button"
      ref={ref}
      className={cx(BUTTON_BASE, BUTTON_VARIANTS[variant], BUTTON_SIZES[size], className)}
      {...props}
    />
  );
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export function Card({
  className,
  children,
  interactive = false,
}: {
  className?: string;
  children: ReactNode;
  interactive?: boolean;
}) {
  return (
    <div
      className={cx(
        // A hairline shadow rather than none: on a tinted page a pure border
        // makes a card look drawn on, and a card look placed.
        'rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgb(25_20_22/0.04)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-none',
        interactive &&
          'transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-lg hover:shadow-slate-900/5 dark:hover:border-slate-700',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">{title}</h1>
        {description && (
          <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-slate-600 dark:text-slate-400">
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------

/** `info` exists because the brand is now red and so is danger: an approved
 *  request sitting beside a rejected one has to be tellable apart at a glance,
 *  and two reds are not. It is NHC's teal, which the palette kept. */
type Tone = 'neutral' | 'brand' | 'copper' | 'success' | 'warning' | 'danger' | 'info';

const BADGE_TONES: Record<Tone, string> = {
  neutral: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  brand: 'bg-brand-100 text-brand-800 dark:bg-brand-900/60 dark:text-brand-200',
  copper: 'bg-copper-100 text-copper-800 dark:bg-copper-900/50 dark:text-copper-200',
  success: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200',
  warning: 'bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-200',
  danger: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200',
  info: 'bg-teal-100 text-teal-900 dark:bg-teal-900/60 dark:text-teal-100',
};

const DOT_TONES: Record<Tone, string> = {
  neutral: 'bg-slate-400',
  brand: 'bg-brand-500',
  copper: 'bg-copper-500',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-red-500',
  info: 'bg-teal-700 dark:bg-teal-100',
};

export function Badge({ tone = 'neutral', dot = false, children }: { tone?: Tone; dot?: boolean; children: ReactNode }) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap',
        BADGE_TONES[tone],
      )}
    >
      {dot && <span className={cx('h-1.5 w-1.5 rounded-full', DOT_TONES[tone])} aria-hidden="true" />}
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Switch: clearer than a checkbox for "this is on for this person"
// ---------------------------------------------------------------------------

/**
 * 44 by 24: on a phone the toggle itself is the whole touch target, and
 * anything smaller than that gets missed. Pass `id` and point a `<label
 * htmlFor>` at it from the visible text, so the words toggle it too.
 */
export function Switch({
  checked,
  onChange,
  disabled,
  label,
  id,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: string;
  id?: string;
}) {
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cx(
        'relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50',
        checked ? 'bg-brand-600' : 'bg-slate-300 dark:bg-slate-700',
      )}
    >
      <span
        aria-hidden="true"
        className={cx(
          'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform',
          checked ? 'translate-x-5.5' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Select
// ---------------------------------------------------------------------------

export function Select({
  value,
  onChange,
  disabled,
  label,
  className,
  children,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  label: string;
  /** Width only: a select otherwise sizes itself to its longest option. */
  className?: string;
  children: ReactNode;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      className={cx(
        'rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-800 transition-colors hover:border-slate-400 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200',
        className,
      )}
    >
      {children}
    </select>
  );
}

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export function Alert({ tone = 'danger', children }: { tone?: 'danger' | 'warning' | 'info'; children: ReactNode }) {
  const tones = {
    danger: 'border-red-200 bg-red-50 text-red-900 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200',
    warning:
      'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200',
    info: 'border-brand-200 bg-brand-50 text-brand-900 dark:border-brand-800 dark:bg-brand-950/50 dark:text-brand-100',
  };
  return (
    <div role="alert" className={cx('rounded-lg border px-4 py-3 text-sm', tones[tone])}>
      {children}
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-sm text-slate-500 dark:text-slate-400">
      <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
        <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      </svg>
      {label}
    </div>
  );
}

/**
 * One treatment for every "there is nothing here" and "that went wrong" screen,
 * so an error never lands as a bare red box on an empty page; it always says
 * what happened and offers the way out.
 */
export function EmptyState({
  icon,
  title,
  body,
  tone = 'neutral',
  action,
}: {
  icon: ReactNode;
  title: string;
  body: ReactNode;
  tone?: 'neutral' | 'success' | 'danger';
  action?: ReactNode;
}) {
  const tones = {
    neutral: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
    success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
    danger: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
  };

  return (
    <Card className="px-6 py-12 text-center">
      <span className={cx('mx-auto flex h-11 w-11 items-center justify-center rounded-full', tones[tone])}>
        {icon}
      </span>
      <h2 className="mt-4 font-medium text-slate-900 dark:text-white">{title}</h2>
      <div className="mx-auto mt-1 max-w-sm text-sm leading-relaxed text-slate-600 dark:text-slate-400">{body}</div>
      {action && <div className="mt-6 flex justify-center gap-3">{action}</div>}
    </Card>
  );
}

export function Avatar({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <span
      aria-hidden="true"
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-800 dark:bg-brand-900 dark:text-brand-200"
    >
      {initials || '?'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Confirmation
// ---------------------------------------------------------------------------

/**
 * "Are you sure?" for actions that change what other people see, or that cannot
 * be undone. The body should say what actually happens rather than repeat the
 * question: someone who has read "this cannot be undone" can decide; someone
 * reading "are you sure?" twice cannot.
 *
 * Initial focus goes to Cancel, so a stray Enter or Space lands on the safe
 * option. Tone is about colour only: `danger` (red) is reserved for what
 * cannot be undone, so that it still means something when it appears.
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  tone = 'danger',
  busy = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  tone?: 'danger' | 'primary';
  busy?: boolean;
  /** For a confirmation that asks the person to type something first. */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  // Cancel takes focus whatever the tone. A confirmation exists because the
  // action deserves a pause; focusing the action button removes the pause.
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape' && !busy) onCancel();
      if (event.key !== 'Tab') return;

      // Keep focus inside the dialog: behind it is a page full of buttons that
      // do things, and tabbing onto one of those while a confirm is open is how
      // people end up clicking something they never saw.
      //
      // Queried from the panel rather than listed: a confirmation that asks you
      // to type the reference has inputs in its body, and a trap that only knew
      // about the two buttons would let you tab straight out past them.
      const focusable = [
        ...(panel.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), a[href]',
        ) ?? []),
      ];
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  return (
    <div
      className="animate-fade fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <div
        ref={panel}
        className="animate-pop w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-800 dark:bg-slate-900"
      >
        <h2 className="text-base font-semibold tracking-tight text-slate-900 dark:text-white">{title}</h2>
        <div className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">{body}</div>

        <div className="mt-6 flex justify-end gap-2">
          <Button ref={cancelRef} variant="secondary" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
          <Button ref={confirmRef} variant={tone} disabled={busy || confirmDisabled} onClick={onConfirm}>
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Says what just happened. Actions here are quiet (a row changes somewhere in
 * a list of thirty-five), so without this it is genuinely unclear whether the
 * click registered.
 */
export function Flash({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 4000);
    return () => clearTimeout(timer);
  }, [message, onDismiss]);

  return (
    <div
      role="status"
      className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4 shrink-0">
        <path d="m5 13 4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span className="min-w-0 flex-1">{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="shrink-0 rounded px-1 text-emerald-700 hover:bg-emerald-100 dark:text-emerald-300 dark:hover:bg-emerald-900/60"
      >
        ×
      </button>
    </div>
  );
}

/**
 * A placeholder shaped like the content that is coming.
 *
 * Better than a spinner for a known layout: the page does not jump when the
 * data lands, and a slow connection looks like loading rather than like
 * nothing happening.
 */
export function Skeleton({ className = 'h-4 w-full' }: { className?: string }) {
  return <div aria-hidden="true" className={`skeleton rounded text-slate-500 ${className}`} />;
}

export function TableSkeleton({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <Card className="overflow-hidden" >
      <div role="status" aria-label="Loading">
        {Array.from({ length: rows }).map((_, row) => (
          <div
            key={row}
            className="flex items-center gap-6 border-b border-slate-100 px-5 py-4 last:border-b-0 dark:border-slate-800/70"
          >
            {Array.from({ length: columns }).map((_, column) => (
              <Skeleton key={column} className={`h-4 ${column === 0 ? 'w-32' : 'flex-1'}`} />
            ))}
          </div>
        ))}
      </div>
    </Card>
  );
}

/**
 * Counts up to its value.
 *
 * Only on arrival, and only for small numbers: the point is to draw the eye
 * to a count that changed, not to make somebody wait to read it. Anything
 * above the threshold, or a reduced-motion preference, renders immediately.
 */
export function CountUp({ value, className = '' }: { value: number; className?: string }) {
  const [shown, setShown] = useState(value);

  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || value > 50 || value === 0) {
      setShown(value);
      return;
    }

    let frame = 0;
    const steps = Math.min(value, 14);
    const timer = setInterval(() => {
      frame += 1;
      setShown(Math.round((value * frame) / steps));
      if (frame >= steps) clearInterval(timer);
    }, 28);
    return () => clearInterval(timer);
  }, [value]);

  return <span className={className}>{shown}</span>;
}
